import { HtmlMarkup, Typography } from '@/core/ui';
import { cn } from '@/lib';
import { API } from '@/store/api';
import type { IWinnerInfo, IPagination } from '@/types';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ISlide2Props {
  initialWinners: IWinnerInfo[];
  initialPagination: IPagination;
}

export const Slide2 = ({ initialWinners, initialPagination }: ISlide2Props) => {
  const [imageIdx, setImageIdx] = useState<number>(0);
  const [allWinners, setAllWinners] = useState<IWinnerInfo[]>(initialWinners);
  const [currentPage, setCurrentPage] = useState(initialPagination.current_page);
  const [pagination, setPagination] = useState<IPagination>(initialPagination);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { t, i18n } = useTranslation('translation');

  const [getCupInfo] = API.cupApi.useLazyGetCupInfoQuery();

  const loadMoreWinners = useCallback(async () => {
    if (isLoadingMore || currentPage >= pagination.last_page) return;

    setIsLoadingMore(true);
    try {
      const response = await getCupInfo({
        locale: i18n.language,
        page: currentPage + 1,
      }).unwrap();

      setAllWinners(prev => [...prev, ...response.winners]);
      setCurrentPage(prev => prev + 1);
      setPagination(response.pagination);
    } catch (error) {
      console.error('Failed to load more winners:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [currentPage, pagination.last_page, isLoadingMore, i18n.language, getCupInfo]);

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const container = e.currentTarget;
    const { scrollTop, scrollHeight, clientHeight } = container;

    const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;

    if (isNearBottom && !isLoadingMore && currentPage < pagination.last_page) {
      loadMoreWinners();
    }
  }, [loadMoreWinners, isLoadingMore, currentPage, pagination.last_page]);

  return (
    <div className='bg-content1 w-full h-screen flex justify-between'>
      <div className='flex flex-col pl-40 pt-16'>
        <Typography className='text-chalk text-[30px] leading-[28.5px] tracking-[1.5px]'>
          {t('cups.winnings')}
        </Typography>
        <div className='overflow-y-scroll no-scrollbar mt-6' ref={scrollContainerRef} onScroll={handleScroll}>
          <div className='fixed overflow-hidden top-22 bg-linear-to-b/srgb from-content1 to to-black/10 blur-2xl w-136 h-40 z-10' />
          <div className='flex flex-col space-y-4 pt-44'>
            {allWinners.map((item, index) => (
              <WinnerCard
                className={cn({ 'bg-olive': imageIdx === index })}
                onClick={() => setImageIdx(index)}
                key={`${item.id}-${index}`}
                data={item}
              />
            ))}
            {isLoadingMore && (
              <div className='flex justify-center py-4'>
                <Typography className='text-chalk/60 text-lg'>
                  Loading...
                </Typography>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className='flex flex-col justify-end items-end'>
        {allWinners[imageIdx]?.image.path && (
          <div className='relative w-[1056px]'>
            <img
              src={allWinners[imageIdx].image.path}
              alt=''
              className='w-full h-[1079px] object-cover'
            />
            {allWinners[imageIdx]?.image_alt && (
              <div className='absolute bottom-0 left-0 right-0 bg-black/70 p-6 flex justify-center items-center'>
                <HtmlMarkup className='text-chalk text-[26px] leading-[29.9px] tracking-[1.3px] text-center whitespace-normal wrap-break-word'>
                  {allWinners[imageIdx].image_alt}
                </HtmlMarkup>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const WinnerCard = ({ data, className, ...rest }: { data: IWinnerInfo } & React.ComponentProps<'button'>) => {
  return (
    <button
      type='button'
      className={cn('flex items-center space-x-4 py-5 px-7 rounded-3xl', className)}
      {...rest}
    >
      <Typography className='text-chalk font-semibold text-[30px] leading-[30px] tracking-[1.5px] pr-8 pl-4'>
        {data.season as string}
      </Typography>
      <div className='flex items-center bg-apple rounded-3xl space-x-2 p-4'>
        <img
          src={data.team_logo.path}
          alt={data.team_logo.caption || ''}
          className='w-[101px] h-[101px]'
        />
        <div className='w-[170px]'>
          <Typography
            className={cn(
              'font-semibold uppercase text-[30px] leading-[30px] tracking-[1.5px]',
              'flex items-center text-left justify-start',
            )}
          >
            {data.team_name}
          </Typography>
        </div>
      </div>
    </button>
  );
};
